import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Camera, ChevronRight, Loader2 } from 'lucide-react';
import Lightbox from '../components/Lightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVENT_COLORS = [
  'event-card-lavender',
  'event-card-cream', 
  'event-card-yellow',
  'event-card-blue',
  'event-card-green',
];

const GalleryPage = () => {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentGroupPhotos, setCurrentGroupPhotos] = useState([]);

  const fetchPhotos = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/photos/grouped`);
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short',
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  const openLightbox = async (photo, groupPhotos) => {
    try {
      const response = await axios.get(`${API}/photos/${photo.id}/url`);
      setSelectedPhoto({
        ...photo,
        fullUrl: response.data.signed_url
      });
      setCurrentGroupPhotos(groupPhotos);
      setLightboxOpen(true);
    } catch (error) {
      console.error('Failed to get photo URL:', error);
    }
  };

  const navigatePhoto = async (direction) => {
    if (!selectedPhoto || !currentGroupPhotos.length) return;
    
    const currentIndex = currentGroupPhotos.findIndex(p => p.id === selectedPhoto.id);
    const newIndex = direction === 'next' 
      ? (currentIndex + 1) % currentGroupPhotos.length
      : (currentIndex - 1 + currentGroupPhotos.length) % currentGroupPhotos.length;
    
    const newPhoto = currentGroupPhotos[newIndex];
    try {
      const response = await axios.get(`${API}/photos/${newPhoto.id}/url`);
      setSelectedPhoto({
        ...newPhoto,
        fullUrl: response.data.signed_url
      });
    } catch (error) {
      console.error('Failed to get photo URL:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="pb-24" data-testid="gallery-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl text-slate-900">Gallery</h1>
            <p className="text-slate-500 text-sm">Welcome back, {user?.name}</p>
          </div>
          <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
            <span className="text-violet-700 font-semibold">
              {user?.name?.charAt(0)?.toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {groups.length === 0 ? (
          <div className="text-center py-16">
            <Camera className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="font-serif text-xl text-slate-600">No photos yet</h3>
            <p className="text-slate-400 mt-2">Photos from church events will appear here</p>
          </div>
        ) : (
          groups.map((group, groupIndex) => (
            <EventCard
              key={`${group.event_date}-${group.event_title}`}
              group={group}
              colorClass={EVENT_COLORS[groupIndex % EVENT_COLORS.length]}
              onPhotoClick={(photo) => openLightbox(photo, group.photos)}
            />
          ))
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && selectedPhoto && (
        <Lightbox
          photo={selectedPhoto}
          onClose={() => setLightboxOpen(false)}
          onPrev={() => navigatePhoto('prev')}
          onNext={() => navigatePhoto('next')}
          hasMultiple={currentGroupPhotos.length > 1}
        />
      )}
    </div>
  );
};

const EventCard = ({ group, colorClass, onPhotoClick }) => {
  const [expanded, setExpanded] = useState(true);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short',
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div 
      data-testid={`event-card-${group.event_date}`}
      className={`${colorClass} rounded-[28px] overflow-hidden shadow-sm animate-slide-up`}
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-5 flex items-center justify-between text-left"
      >
        <div>
          <p className="text-slate-500 text-sm">{formatDate(group.event_date)}</p>
          <h3 className="font-serif text-xl text-slate-900 mt-1">{group.event_title}</h3>
          <div className="flex items-center gap-2 mt-2">
            <Camera className="w-4 h-4 text-slate-400" />
            <span className="text-slate-500 text-sm">{group.total_count} photos</span>
          </div>
        </div>
        <ChevronRight 
          className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} 
        />
      </button>

      {/* Photo Grid */}
      {expanded && group.photos.length > 0 && (
        <div className="px-4 pb-4">
          <div className="photo-grid">
            {group.photos.slice(0, 9).map((photo, index) => (
              <button
                key={photo.id}
                data-testid={`photo-${photo.id}`}
                onClick={() => onPhotoClick(photo)}
                className="photo-grid-item bg-slate-200"
              >
                <img 
                  src={photo.thumbnail_url} 
                  alt={photo.file_name}
                  loading="lazy"
                />
              </button>
            ))}
          </div>
          {group.total_count > 9 && (
            <p className="text-center text-slate-500 text-sm mt-3">
              +{group.total_count - 9} more photos
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
